import { Injectable } from '@angular/core'
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http'
import { Observable, throwError, empty } from 'rxjs'
import { HttpUrlEncodingCodec } from './../services/auth/encoder'
import { Broker } from '@models/broker.model'
import { PossibleValue } from '@models/possible-value.model'
import { environment } from '@env/environment'
const BASE_PATH = environment.basePath
/**
 * Service class to communicate with the REST services
 */
@Injectable({
  providedIn: 'root',
})
export class BrokerService {
  constructor(
    protected httpClient: HttpClient,
    protected encoder: HttpUrlEncodingCodec
  ) { }
  /**
   * Export the selected elements with the givenFormat
   * @param {string} format
   * @param {string} keys
   * @param {string} criteria
   * @param {string} searchOrder
   * @param {Broker} body
   * @returns {  Observable<File>}
   */
  export(
    format?: string,
    keys?: string,
    criteria?: string,
    searchOrder?: string,
    body?: Broker[]
  ): Observable<any> {
    let params = new HttpParams({ encoder: this.encoder })
    if (format !== undefined && format !== null) {
      params = params.append('format', format.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (searchOrder !== undefined && searchOrder !== null) {
      params = params.append('searchOrder', searchOrder.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post(`${BASE_PATH}/brokers/export`, body, {
      params: params,
      headers: headers,
      observe: 'response',
      responseType: 'blob',
    })
  }
  /**
   * Validate the integrity of all fields of the given com.vermeg.portal.operation.common.Broker
   * @param {Broker} body
   * @returns {  Observable<any>}
   */
  validate(body?: Broker): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/brokers/validate`, body, {
      headers: headers,
    })
  }
  /**
   * Counts all the brokers with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  count(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    return this.httpClient.get<number>(`${BASE_PATH}/brokers/count`, {
      params: params,
    })
  }
  /**
   * Find by the broker identifiers
   * @param {string} keys the required keys
   * @returns {Observable<Broker>}
   */
  findByCode(keys?: string): Observable<Broker> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<Broker>(`${BASE_PATH}/brokers`, {
      params: params,
    })
  }
  /**
   * Saves the broker
   * @param {Broker} body
   * @returns {Observable<Broker>}
   */
  insert(body?: Broker): Observable<Broker> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<Broker>(`${BASE_PATH}/brokers`, body, {
      headers: headers,
    })
  }
  /**
   * Updates the broker
   * @param {Broker} body
   * @returns {Observable<Broker>}
   */
  update(body?: Broker): Observable<Broker> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.put<Broker>(`${BASE_PATH}/brokers`, body, {
      headers: headers,
    })
  }
  /**
   * Deletes all brokers with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  delete(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/brokers`, {
      params: params,
    })
  }
  /**
   * Return a new broker instance initialized from the server
   * @returns {Observable<Broker>}
   */
  newInstance(): Observable<Broker> {
    return this.httpClient.get<Broker>(`${BASE_PATH}/brokers/new`)
  }
  /**
   * Performs a paginated search on brokers
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @param {number} page the page number
   * @param {number} size the page size
   * @returns {  Observable<any[]>}
   */
  find(
    criteria?: string,
    order?: string,
    keys?: string,
    page?: number,
    size?: number
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (order !== undefined && order !== null) {
      params = params.append('order', order.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (page !== undefined && page !== null) {
      params = params.append('page', page.toString())
    }
    if (size !== undefined && size !== null) {
      params = params.append('size', size.toString())
    }
    return this.httpClient.get<any[]>(`${BASE_PATH}/brokers/search`, {
      params: params,
    })
  }
  /**
   * Returns the possible values of the given field
   * @param {string} field
   * @param {string} criteria
   * @param {Broker} body
   * @returns {  Observable<any[]>}
   */
  getPossibleValues(
    field: string,
    criteria?: string,
    body?: Broker
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/brokers/${field}/possibleValues`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Performs a search operation without pagination
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @returns {  Observable<any[]>}
   */
  findAll(criteria?: string, order?: string, keys?: string): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    if (order !== undefined && order !== null) {
      params = params.append('order', order.toString())
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<any[]>(`${BASE_PATH}/bff/corretora/all`, {
      params: params,
    })
  }
  /**
   * Returns the possible values of the given field translated using the current user profile
   * @param {string} field
   * @param {string} keys
   * @param {string} seperator
   * @param {string} criteria
   * @param {Broker} body
   * @returns {  Observable<any[]>}
   */
  getTranslatedPossibleValues(
    field: string,
    keys?: string,
    seperator?: string,
    criteria?: string,
    body?: Broker
  ): Observable<any[]> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    if (seperator !== undefined && seperator !== null) {
      params = params.append('seperator', seperator.toString())
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/brokers/${field}/possibleValues/translated`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Find the broker by code
   * @param {string} code
   * @param {string} keys the required keys
   * @returns {Observable<Broker>}
   */
  findByCodeAsString(code: string, keys?: string): Observable<Broker> {
    let params = new HttpParams({ encoder: this.encoder })
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', keys.toString())
    }
    return this.httpClient.get<Broker>(`${BASE_PATH}/brokers/${code}`, {
      params: params,
    })
  }
  /**
   * Deletes the broker instance with the given code
   * @param {string} code
   * @returns {  Observable<any>}
   */
  deleteOne(code: string): Observable<any> {
    return this.httpClient.delete<any>(`${BASE_PATH}/brokers/${code}`)
  }
  /**
   * Deletes all brokers with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  deleteWithCriteria(criteria?: string): Observable<number> {
    let params = new HttpParams({ encoder: this.encoder })
    if (criteria !== undefined && criteria !== null) {
      params = params.append('criteria', criteria.toString())
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/brokers/delete`, {
      params: params,
    })
  }
  /**
   * Validate the integrity of the fieldName in the given broker instance
   * @param {string} field
   * @param {Broker} body
   * @returns {  Observable<any>}
   */
  validateField(field: string, body?: Broker): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(
      `${BASE_PATH}/brokers/${field}/validate`,
      body,
      { headers: headers }
    )
  }
  /**
   * Validate the integrity of all fields of the given Broker
   * @param {Broker} body
   * @returns {  Observable<any>}
   */
  validateAll(body?: Broker): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(
      `${BASE_PATH}/brokers/validate/all`,
      body,
      { headers: headers }
    )
  }
  /**
   * Evaluates the given formula against the given broker instance
   * @param {string} formula
   * @param {Broker} body
   * @returns {  Observable<any>}
   */
  evaluateFormula(formula?: string, body?: Broker): Observable<any> {
    let params = new HttpParams({ encoder: this.encoder })
    if (formula !== undefined && formula !== null) {
      params = params.append('formula', formula.toString())
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/brokers/evaluate`, body, {
      params: params,
      headers: headers,
    })
  }
}
